import { InventoryItemCollectionProps } from "./InventoryItemCollection";
import InventoryItemTransferContext from "../../../../utils/sys/InventoryItemTransferContext";
import FlexColumn from "../../../../common/components/layout/Flexbox/FlexColumn";
import InventoryItemListSlotGap from "./InventoryItemListSlotGap";
import InventoryItemInfoSlot from "../InventoryItemSlot/InventoryItemInfoSlot";
import { ReactNode, useCallback, useContext, useMemo, useRef } from "react";

import {
    executeTransfers,
    validateTransfer,
} from "../../../../utils/sys/InventoryItemTransferUtils";
import InventoryItem from "../../../../utils/game/mechanics/InventoryItem";
import { UIInventoryItemCollectionProps } from "./InventoryItemCollection";

import "./style/InventoryItemList_style.css";

type Props = UIInventoryItemCollectionProps & {
    onItemEditRequested?: (itemIndex: number) => void;
    onItemChanged?: (
        item: InventoryItem | null,
        index: number,
        mode: "insert" | "replace",
        isInSequence: boolean
    ) => void;
    onItemsReordered?: (reorderedItems: Array<InventoryItem>) => void;
    onTransferSequenceComplete?: () => void;
};

export default function InventoryItemList({
    id,
    items,
    isEditable = false,
    itemRestrictor,
    onItemEditRequested,
    onItemChanged,
    onItemsReordered,
    onTransferSequenceComplete,
}: Props): JSX.Element {
    const ref = useRef<HTMLDivElement>(null);

    const { transferTargetItemContainer, setTransferTargetItemContainer } =
        useContext(InventoryItemTransferContext);

    const storedItemEquipTypes = useMemo(() => {
        return transferTargetItemContainer?.item?.equipTypes ?? [];
    }, [transferTargetItemContainer]);

    const showSlotGaps = useMemo(() => {
        return transferTargetItemContainer?.item != null;
    }, [transferTargetItemContainer]);

    const listElements = useMemo((): ReactNode => {
        const createSlotGap = (gapIndex: number) => {
            return (
                <InventoryItemListSlotGap
                    key={`slot_gap_${id}-${gapIndex}`}
                    onClick={(_) => {
                        if (
                            isEditable &&
                            transferTargetItemContainer?.item != null
                        ) {
                            if (
                                transferTargetItemContainer.collectionID === id
                            ) {
                                const oldIndex = items.indexOf(
                                    transferTargetItemContainer.item
                                );

                                if (oldIndex >= 0 && oldIndex !== gapIndex) {
                                    let reorderedItems = [...items];
                                    reorderedItems.splice(
                                        gapIndex,
                                        0,
                                        transferTargetItemContainer.item
                                    );
                                    reorderedItems.splice(
                                        oldIndex > gapIndex
                                            ? oldIndex + 1
                                            : oldIndex,
                                        1
                                    );

                                    onItemsReordered?.(reorderedItems);
                                }
                            } else {
                                const insertParams = {
                                    id: `slot_gap_${id}-${gapIndex}`,
                                    collectionID: id,
                                    item: null,
                                    itemRestrictor,
                                    setItemCallback: (
                                        item: InventoryItem | null
                                    ) => {
                                        onItemChanged?.(
                                            item,
                                            gapIndex,
                                            "insert",
                                            true
                                        );
                                    },
                                };
                                if (
                                    validateTransfer(
                                        transferTargetItemContainer,
                                        insertParams
                                    )
                                ) {
                                    executeTransfers({
                                        transfers: [
                                            {
                                                item: null,
                                                setItemCallback:
                                                    transferTargetItemContainer
                                                        .transferVariables
                                                        ?.setItemCallback,
                                            },
                                            {
                                                item: transferTargetItemContainer.item,
                                                setItemCallback:
                                                    insertParams.setItemCallback,
                                            },
                                        ],
                                        onComplete: () => {
                                            onTransferSequenceComplete?.();

                                            if (
                                                transferTargetItemContainer
                                                    .transferVariables
                                                    ?.onTransferSequenceComplete !=
                                                    null &&
                                                transferTargetItemContainer
                                                    .transferVariables
                                                    ?.onTransferSequenceComplete !==
                                                    onTransferSequenceComplete
                                            ) {
                                                transferTargetItemContainer.transferVariables.onTransferSequenceComplete();
                                            }
                                        },
                                    });
                                }
                            }
                            setTransferTargetItemContainer(null);
                        }
                    }}
                />
            );
        };

        const listItems = [
            ...(transferTargetItemContainer?.item != null &&
            (itemRestrictor == null ||
                itemRestrictor(transferTargetItemContainer.item)) &&
            items.length === 0
                ? [createSlotGap(0)]
                : []
            ).flat(),
            ...items.flatMap((item, index) => {
                const elements: Array<ReactNode> = [];
                const slotID = `${id}-${index}`;

                const itemElement = (
                    <InventoryItemInfoSlot
                        id={slotID}
                        collectionID={id}
                        key={slotID}
                        className="item-slot"
                        item={item}
                        tooltipEnabled={true}
                        isEditable={isEditable}
                        itemRestrictor={itemRestrictor}
                        transferVariables={{
                            setItemCallback: (item) => {
                                onItemChanged?.(item, index, "replace", true);
                            },
                            onTransferSequenceComplete:
                                onTransferSequenceComplete,
                        }}
                        onClick={(payload) => {
                            if (isEditable && payload.target != null) {
                                if (transferTargetItemContainer == null) {
                                    setTransferTargetItemContainer(
                                        payload.target
                                    );
                                } else {
                                    if (
                                        transferTargetItemContainer.id ===
                                        slotID
                                    ) {
                                        // Initial item selection
                                        setTransferTargetItemContainer(null);
                                    } else {
                                        if (
                                            payload.target?.collectionID !=
                                                null &&
                                            payload.target.collectionID ===
                                                transferTargetItemContainer.collectionID
                                        ) {
                                            // Internal item swap
                                            if (
                                                payload.target.item != null &&
                                                transferTargetItemContainer.item !=
                                                    null
                                            ) {
                                                const item1 =
                                                    payload.target.item;
                                                const item2 =
                                                    transferTargetItemContainer.item;
                                                const index1 =
                                                    items.indexOf(item1);
                                                const index2 =
                                                    items.indexOf(item2);

                                                if (
                                                    index1 >= 0 &&
                                                    index2 >= 0 &&
                                                    index1 !== index2
                                                ) {
                                                    onItemsReordered?.(
                                                        [...items].map(
                                                            (item, index) =>
                                                                index === index1
                                                                    ? item2
                                                                    : index ===
                                                                      index2
                                                                    ? item1
                                                                    : item
                                                        )
                                                    );
                                                }
                                            }
                                        } else {
                                            // Container-to-container transfer
                                            if (
                                                validateTransfer(
                                                    transferTargetItemContainer,
                                                    payload.target
                                                )
                                            ) {
                                                executeTransfers({
                                                    transfers: [
                                                        {
                                                            item: transferTargetItemContainer.item,
                                                            setItemCallback:
                                                                payload.target
                                                                    .transferVariables
                                                                    ?.setItemCallback,
                                                        },
                                                        {
                                                            item: payload.target
                                                                .item,
                                                            setItemCallback:
                                                                transferTargetItemContainer
                                                                    .transferVariables
                                                                    ?.setItemCallback,
                                                        },
                                                    ],
                                                    onComplete: () => {
                                                        onTransferSequenceComplete?.();

                                                        if (
                                                            transferTargetItemContainer
                                                                .transferVariables
                                                                ?.onTransferSequenceComplete !=
                                                                null &&
                                                            transferTargetItemContainer
                                                                .transferVariables
                                                                ?.onTransferSequenceComplete !==
                                                                onTransferSequenceComplete
                                                        ) {
                                                            transferTargetItemContainer.transferVariables.onTransferSequenceComplete();
                                                        }
                                                    },
                                                });
                                            }
                                        }

                                        setTransferTargetItemContainer(null);
                                    }
                                }
                            }
                        }}
                        isSelected={
                            transferTargetItemContainer?.id !== null &&
                            transferTargetItemContainer?.id === slotID
                        }
                        isValidTarget={
                            transferTargetItemContainer?.item != null &&
                            item.equipTypes.find((t) =>
                                storedItemEquipTypes.includes(t)
                            ) != null
                        }
                        onEditRequested={() => {
                            onItemEditRequested?.(index);
                        }}
                    />
                );

                if (
                    index === 0 &&
                    showSlotGaps &&
                    transferTargetItemContainer?.item != null &&
                    (itemRestrictor == null ||
                        itemRestrictor(transferTargetItemContainer.item))
                ) {
                    if (
                        transferTargetItemContainer?.item?.universalID !==
                        items[0].universalID
                    ) {
                        elements.push(createSlotGap(0));
                    }
                }

                elements.push(itemElement);

                const gapIndex = index + 1;
                if (
                    showSlotGaps &&
                    transferTargetItemContainer?.item != null &&
                    (itemRestrictor == null ||
                        itemRestrictor(transferTargetItemContainer.item))
                ) {
                    if (
                        transferTargetItemContainer?.item.universalID !==
                        item.universalID
                    ) {
                        const nextItem =
                            gapIndex < items.length ? items[gapIndex] : null;
                        if (
                            nextItem == null ||
                            transferTargetItemContainer?.item?.universalID !==
                                nextItem.universalID
                        )
                            elements.push(createSlotGap(gapIndex));
                    }
                }

                return elements;
            }),
        ];

        return listItems;
    }, [
        items,
        showSlotGaps,
        setTransferTargetItemContainer,
        transferTargetItemContainer,
    ]);

    return (
        <FlexColumn forwardRef={ref} className="inventory-item-list-container">
            {listElements}
        </FlexColumn>
    );
}
